import React, { useEffect, useMemo, useState } from "react";
import { X, Loader2, AlertTriangle } from "lucide-react";
import dayjs from "dayjs";
import Drawer from "../overlays/Drawer";
import { Button, IconButton, Input, Textarea, FieldRow, Badge } from "../atoms";
import { useCreateLead, useUpdateLead, useDuplicateCheck } from "../hooks/useLeads";
import { useOrgCurrency } from "../hooks/useOrgCurrency";
import { DEFAULT_CURRENCY } from "../lib/currency";
import MemberPicker from "../molecules/MemberPicker";
import FollowUpDateTime, { defaultFollowUpValue } from "../molecules/FollowUpDateTime";
import { useMyCrmRole } from "../hooks/useTeamMembers";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (v) => EMAIL_RE.test(String(v || "").trim());

// Allow digits, +, -, (, ), spaces, dots — no letters.
const PHONE_ALLOWED_RE = /^[0-9+\-().\s]*$/;
const isValidPhone = (v) => !v || PHONE_ALLOWED_RE.test(String(v));

// Tiny inline debounce — avoids pulling in lodash. Returns the latest value
// after `ms` of quiet. Used to throttle the duplicate-check API call.
function useDebounced(value, ms = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// A lead with no next follow-up is a lead nobody is working, so the date is a
// required field here — pre-filled with tomorrow 9am (the same default the API
// applies) so it costs nothing when the user doesn't care. Only won and lost
// leads are exempt; there is no next step after a deal closes.
const isClosed = (lead) => lead?.status === "won" || lead?.status === "lost";

function makeEmpty(defaultCurrency = DEFAULT_CURRENCY) {
  return {
    title: "",
    brandName: "",
    email: "",
    phone: "",
    website: "",
    value: "",
    currency: defaultCurrency,
    source: "Manually created",
    nextActivity: "",
    labels: "",
    comments: "",
    ownerId: null,
    followUpAt: defaultFollowUpValue(),
  };
}

function leadToForm(lead, defaultCurrency = DEFAULT_CURRENCY) {
  if (!lead) return makeEmpty(defaultCurrency);
  const pending = lead.followUp?.status === "pending" && lead.followUp.scheduledAt;
  return {
    title: lead.title || "",
    brandName: lead.brandName || "",
    email: lead.email || "",
    phone: lead.phone || "",
    website: lead.website || "",
    value: lead.value ?? "",
    currency: lead.currency || defaultCurrency,
    source: lead.source || "Manually created",
    nextActivity: lead.nextActivity || "",
    labels: (lead.labels || []).join(", "),
    comments: lead.comments || "",
    ownerId: lead.ownerId ? String(lead.ownerId) : null,
    followUpAt: pending
      ? dayjs(lead.followUp.scheduledAt)
      : (isClosed(lead) ? null : defaultFollowUpValue()),
  };
}

function formToPayload(form, defaultCurrency = DEFAULT_CURRENCY) {
  return {
    title: form.title.trim(),
    brandName: form.brandName.trim() || null,
    email: form.email.trim() || undefined,
    phone: form.phone.trim() || undefined,
    website: form.website.trim() || undefined,
    value: form.value === "" ? undefined : Number(form.value),
    currency: form.currency || defaultCurrency,
    source: form.source || "Manually created",
    nextActivity: form.nextActivity.trim() || undefined,
    labels: form.labels.split(",").map((s) => s.trim()).filter(Boolean),
    comments: form.comments.trim() || undefined,
    ownerId: form.ownerId || null,
    followUpAt: form.followUpAt?.isValid?.() ? form.followUpAt.toDate().toISOString() : undefined,
    followUpTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
  };
}

export default function LeadFormModal({ open, onClose, lead = null, onSaved }) {
  const editing = !!lead;
  const { currency: orgCurrency } = useOrgCurrency();
  const { isOwner } = useMyCrmRole();
  const [form, setForm] = useState(() => makeEmpty(orgCurrency));
  const [error, setError] = useState(null);

  const createMut = useCreateLead();
  const updateMut = useUpdateLead();
  const busy = createMut.isPending || updateMut.isPending;

  useEffect(() => {
    if (open) {
      setForm(leadToForm(lead, orgCurrency));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead, orgCurrency]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPhone = (e) => {
    const raw = e.target.value;
    // Strip any alphabetic characters immediately — give tactile feedback
    const cleaned = raw.replace(/[a-zA-Z]/g, "");
    setForm((f) => ({ ...f, phone: cleaned }));
  };
  const phoneError = form.phone && !isValidPhone(form.phone) ? "Phone must contain only digits, +, -, (, ), spaces" : null;

  // Only check for duplicates on create (would always self-match in edit).
  // 400ms debounce on every keystroke that could trigger a match.
  const dupKey = useDebounced({
    email: form.email.trim().toLowerCase(),
    phone: form.phone.trim(),
    brandName: form.brandName.trim(),
  }, 400);
  const dupQuery = useDuplicateCheck(editing ? {} : dupKey);
  const dupes = useMemo(() => (editing ? [] : (dupQuery.data || [])), [editing, dupQuery.data]);
  // A same-email match is a HARD block (backend rejects it too). Phone/brand
  // matches stay soft warnings. Editing never self-blocks.
  const emailDup = useMemo(() => {
    const e = form.email.trim().toLowerCase();
    if (editing || !e) return null;
    return dupes.find((d) => (d.email || "").trim().toLowerCase() === e) || null;
  }, [editing, form.email, dupes]);
  // Closed deals skip the follow-up requirement; everything else must carry one.
  const followUpRequired = !isClosed(lead);
  const followUpMissing = followUpRequired && !form.followUpAt?.isValid?.();
  const canSubmit = !!form.title.trim() && isValidEmail(form.email) && !emailDup
    && !phoneError && !followUpMissing;

  const submit = async (e) => {
    e?.preventDefault();
    setError(null);
    const payload = formToPayload(form, orgCurrency);
    if (!payload.title) {
      setError("Title is required");
      return;
    }
    if (!isValidEmail(form.email)) {
      setError("A valid email is required");
      return;
    }
    if (!isValidPhone(form.phone)) {
      setError("Phone must contain only digits and characters like +, -, (, )");
      return;
    }
    if (followUpMissing) {
      setError("Pick the next follow-up date — required unless the lead is won or lost");
      return;
    }
    try {
      const data = editing
        ? await updateMut.mutateAsync({ id: lead._id, patch: payload })
        : await createMut.mutateAsync(payload);
      onSaved?.(data.lead);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || "Save failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  };

  return (
    <Drawer open={open} onClose={busy ? undefined : onClose} width={560}>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 22px", borderBottom: "1px solid var(--color-border-subtle)", flexShrink: 0 }}>
          <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0, color: "var(--color-text-primary)" }}>
            {editing ? "Edit lead" : "New lead"}
          </h2>
          <IconButton type="button" aria-label="Close" onClick={onClose}><X size={18} /></IconButton>
        </header>

        <div style={{ padding: "18px 22px", overflowY: "auto", flex: 1, minHeight: 0 }}>
          <FieldRow label="Brand / Company" htmlFor="lead-brand">
            <Input id="lead-brand" autoFocus value={form.brandName} onChange={set("brandName")} placeholder="Acme Corp" />
          </FieldRow>
          <FieldRow label="Contact person name" htmlFor="lead-title" required>
            <Input id="lead-title" value={form.title} onChange={set("title")} placeholder="e.g. John Smith" aria-required="true" required />
          </FieldRow>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <FieldRow label="Email" htmlFor="lead-email" required>
              <Input id="lead-email" type="email" value={form.email} onChange={set("email")} aria-required="true" required />
            </FieldRow>
            <FieldRow label="Phone" htmlFor="lead-phone">
              <Input id="lead-phone" value={form.phone} onChange={setPhone} placeholder="+91 98765 43210" inputMode="tel" />
              {phoneError && <div style={{ fontSize: 11, color: "var(--crm-danger, #DC2626)", marginTop: 3 }}>{phoneError}</div>}
            </FieldRow>
          </div>
          {/* Sits this high on purpose: it is the field that decides whether
              anyone ever speaks to this lead again. */}
          {followUpRequired ? (
            <FieldRow
              label="Next follow-up"
              required
              hint="When will you contact them next? Required until the lead is won or lost."
            >
              <FollowUpDateTime
                value={form.followUpAt}
                onChange={(v) => setForm((f) => ({ ...f, followUpAt: v }))}
              />
              {form.followUpAt?.isValid?.() && (
                <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 6 }}>
                  {form.followUpAt.format("ddd, D MMM h:mm A")}
                </div>
              )}
            </FieldRow>
          ) : (
            <FieldRow label="Next follow-up">
              <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
                Not needed — this lead is {lead.status}.
              </div>
            </FieldRow>
          )}
          <FieldRow label="Website" htmlFor="lead-website">
            <Input id="lead-website" value={form.website} onChange={set("website")} placeholder="example.com" />
          </FieldRow>
          {/* Deal value — currency is implicit (org default, set in
              Settings → Currency). No per-lead override in the form. */}
          <FieldRow label="Deal value" htmlFor="lead-value">
            <Input id="lead-value" type="number" min={0} step="any" value={form.value} onChange={set("value")} placeholder="0" />
          </FieldRow>
          <FieldRow label="Labels" htmlFor="lead-labels" hint="Comma-separated">
            <Input id="lead-labels" value={form.labels} onChange={set("labels")} placeholder="Enterprise, Q3, Hot" />
          </FieldRow>
          <FieldRow label="Next activity" htmlFor="lead-next">
            <Input id="lead-next" value={form.nextActivity} onChange={set("nextActivity")} placeholder="e.g. Send pricing PDF" />
          </FieldRow>
          <FieldRow label="Source" htmlFor="lead-source">
            <Input id="lead-source" value={form.source} onChange={set("source")} />
          </FieldRow>
          {isOwner && (
            <FieldRow label="Owner">
              <MemberPicker
                value={form.ownerId}
                onChange={(id) => setForm((f) => ({ ...f, ownerId: id }))}
                width={300}
              />
            </FieldRow>
          )}
          <FieldRow label="Comments" htmlFor="lead-comments">
            <Textarea id="lead-comments" value={form.comments} onChange={set("comments")} />
          </FieldRow>

          {dupes.length > 0 && (
            <div style={{ background: "var(--crm-warning-soft, #FEF3C7)", border: "1px solid var(--crm-warning, #D97706)", color: "var(--color-text-primary)", padding: 12, borderRadius: 8, fontSize: 13, marginTop: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontWeight: 600 }}>
                <AlertTriangle size={14} style={{ color: "var(--crm-warning, #D97706)" }} />
                Possible duplicate{dupes.length === 1 ? "" : "s"} found
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {dupes.map((d) => (
                  <div key={d._id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                    <div style={{ fontWeight: 500 }}>{d.title}</div>
                    {d.brandName && <span style={{ color: "var(--color-text-secondary)" }}>· {d.brandName}</span>}
                    {d.email && <span style={{ color: "var(--color-text-secondary)" }}>· {d.email}</span>}
                    <Badge tone={d.status === "won" ? "success" : d.status === "lost" ? "danger" : "primary"}>{d.status}</Badge>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: emailDup ? "var(--crm-danger)" : "var(--color-text-secondary)", fontWeight: emailDup ? 600 : 400 }}>
                {emailDup
                  ? "A lead with this email already exists — change the email to create."
                  : "You can still create — this is a warning, not a block."}
              </div>
            </div>
          )}

          {error && (
            <div style={{ background: "var(--crm-danger-soft)", color: "var(--crm-danger)", padding: 10, borderRadius: 8, fontSize: 13, marginTop: 4 }}>
              {error}
            </div>
          )}
        </div>

        <footer style={{ padding: "14px 22px", borderTop: "1px solid var(--color-border-subtle)", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, flexShrink: 0, background: "var(--color-bg-base)" }}>
          <Button type="button" variant="secondary" size="md" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" variant="primary" size="md" disabled={busy || !canSubmit} leftIcon={busy ? <Loader2 size={15} className="animate-spin" /> : null}>
            {editing ? "Save changes" : "Create lead"}
          </Button>
        </footer>
      </form>
    </Drawer>
  );
}
